/**
 * TemplateEditor - Éditeur de template de facture WYSIWYG
 * 
 * Ce composant permet de configurer un template de facture avec :
 * - Couleurs (primary, secondary, accent)
 * - Logo (upload, dimensions)
 * - Éléments (drag & drop pour réorganiser, toggle visibility)
 * 
 * La preview correspond EXACTEMENT au PDF généré.
 */

import React, { useState, useRef } from 'react';
import { 
  X, Save, Upload, Trash2, Eye, EyeOff, 
  GripVertical, Palette, Image, LayoutList, Settings
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Helper pour convertir les URLs relatives en URLs complètes
const getFullLogoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Types d'éléments disponibles
const ELEMENT_TYPES = {
  header: { label_fr: 'En-tête', label_nl: 'Koptekst', icon: '📄' },
  client_info: { label_fr: 'Client', label_nl: 'Klant', icon: '👤' },
  items_table: { label_fr: 'Articles', label_nl: 'Artikelen', icon: '📋' },
  totals: { label_fr: 'Totaux', label_nl: 'Totalen', icon: '💰' },
  payment_info: { label_fr: 'Paiement', label_nl: 'Betaling', icon: '🏦' },
  footer: { label_fr: 'Pied de page', label_nl: 'Voettekst', icon: '📝' },
};

// Éléments par défaut
const DEFAULT_ELEMENTS = [
  { id: 'header', type: 'header', visible: true },
  { id: 'client_info', type: 'client_info', visible: true },
  { id: 'items_table', type: 'items_table', visible: true },
  { id: 'totals', type: 'totals', visible: true },
  { id: 'payment_info', type: 'payment_info', visible: true },
  { id: 'footer', type: 'footer', visible: true },
];

// Composant pour un élément draggable
const SortableElement = ({ element, language, onToggleVisibility }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: element.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const typeInfo = ELEMENT_TYPES[element.type] || { label_fr: element.type, label_nl: element.type, icon: '📄' };
  const label = language === 'fr' ? typeInfo.label_fr : typeInfo.label_nl;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 bg-white border rounded-lg ${
        element.visible ? 'border-gray-200' : 'border-gray-100 bg-gray-50'
      }`}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
      >
        <GripVertical className="w-5 h-5" />
      </button>
      
      <span className="text-xl">{typeInfo.icon}</span>
      
      <span className={`flex-1 ${element.visible ? 'text-gray-900' : 'text-gray-400'}`}>
        {label}
      </span>
      
      <button
        onClick={() => onToggleVisibility(element.id)}
        className={`p-1.5 rounded ${
          element.visible 
            ? 'text-green-600 hover:bg-green-50' 
            : 'text-gray-400 hover:bg-gray-100'
        }`}
      >
        {element.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
      </button>
    </div>
  );
};

// Preview du template - correspond EXACTEMENT au PDF
const TemplatePreview = ({ template, language }) => {
  const t = language === 'fr' ? {
    invoice: 'FACTURE',
    number: 'N°',
    date: 'Date',
    dueDate: 'Échéance',
    billedTo: 'Facturé à:',
    description: 'Description',
    qty: 'Qté',
    priceHT: 'Prix HTVA',
    total: 'Total',
    subtotal: 'Sous-total HTVA',
    vat: 'TVA 21%',
    totalTTC: 'TOTAL TTC',
    paymentInfo: 'INFORMATIONS DE PAIEMENT',
    iban: 'IBAN',
    bic: 'BIC',
    scanToPay: 'Scannez pour payer',
    footer: 'Merci pour votre confiance.',
  } : {
    invoice: 'FACTUUR',
    number: 'Nr.',
    date: 'Datum',
    dueDate: 'Vervaldatum',
    billedTo: 'Gefactureerd aan:',
    description: 'Omschrijving',
    qty: 'Aantal',
    priceHT: 'Prijs excl.',
    total: 'Totaal',
    subtotal: 'Subtotaal excl.',
    vat: 'BTW 21%',
    totalTTC: 'TOTAAL incl.',
    paymentInfo: 'BETALINGSINFORMATIE',
    iban: 'IBAN',
    bic: 'BIC',
    scanToPay: 'Scan om te betalen',
    footer: 'Bedankt voor uw vertrouwen.',
  };

  const primary = template.primary_color || '#8B4513';
  const secondary = template.secondary_color || '#D2691E';
  const accent = template.accent_color || '#F5DEB3';
  
  const elements = template.elements || DEFAULT_ELEMENTS;
  const getElement = (type) => elements.find(e => e.type === type);
  const isVisible = (type) => getElement(type)?.visible !== false;

  return (
    <div className="bg-white border rounded-lg shadow-sm p-6 text-xs" style={{ minHeight: '500px' }}>
      {/* HEADER */}
      {isVisible('header') && (
        <div className="flex justify-between items-start mb-6 pb-4 border-b-2" style={{ borderColor: primary }}>
          <div>
            {template.logo_url ? (
              <img 
                src={getFullLogoUrl(template.logo_url)} 
                alt="Logo" 
                className="max-h-16 max-w-32 object-contain mb-2"
                onError={(e) => {
                  console.log('Logo load error:', template.logo_url);
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-20 h-12 bg-gray-100 rounded flex items-center justify-center text-gray-400 mb-2">
                LOGO
              </div>
            )}
            <div className="text-lg font-bold" style={{ color: primary }}>{t.invoice}</div>
            <div className="text-gray-600 mt-1">
              <div>Votre Entreprise</div>
              <div>123 Rue Exemple</div>
              <div>1000 Bruxelles</div>
            </div>
          </div>
          <div className="text-right">
            <div><span className="font-semibold">{t.number}:</span> FAC-2024-0001</div>
            <div><span className="font-semibold">{t.date}:</span> 01/01/2024</div>
            <div><span className="font-semibold">{t.dueDate}:</span> 31/01/2024</div>
          </div>
        </div>
      )}

      {/* CLIENT INFO - Aligné à droite */}
      {isVisible('client_info') && (
        <div className="mb-6 flex justify-end">
          <div className="p-3 rounded w-48" style={{ backgroundColor: `${accent}40` }}>
            <div className="font-semibold mb-1">{t.billedTo}</div>
            <div>Nom du Client</div>
            <div>456 Avenue Client</div>
            <div>2000 Anvers</div>
          </div>
        </div>
      )}

      {/* ITEMS TABLE */}
      {isVisible('items_table') && (
        <table className="w-full mb-6">
          <thead>
            <tr style={{ backgroundColor: primary, color: 'white' }}>
              <th className="text-left p-2 rounded-tl">{t.description}</th>
              <th className="text-center p-2 w-16">{t.qty}</th>
              <th className="text-right p-2 w-24">{t.priceHT}</th>
              <th className="text-right p-2 w-24 rounded-tr">{t.total}</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ backgroundColor: accent }}>
              <td className="p-2">Article exemple</td>
              <td className="text-center p-2">2</td>
              <td className="text-right p-2">€20.66</td>
              <td className="text-right p-2">€41.32</td>
            </tr>
            <tr>
              <td className="p-2">Autre article</td>
              <td className="text-center p-2">1</td>
              <td className="text-right p-2">€61.98</td>
              <td className="text-right p-2">€61.98</td>
            </tr>
          </tbody>
        </table>
      )}

      {/* TOTALS */}
      {isVisible('totals') && (
        <div className="flex justify-end mb-6">
          <div className="w-48">
            <div className="flex justify-between py-1">
              <span>{t.subtotal}:</span>
              <span>€103.30</span>
            </div>
            <div className="flex justify-between py-1" style={{ color: secondary }}>
              <span>{t.vat}:</span>
              <span>€21.69</span>
            </div>
            <div 
              className="flex justify-between py-2 font-bold text-sm mt-1 px-2 rounded"
              style={{ backgroundColor: accent, borderTop: `2px solid ${primary}` }}
            >
              <span>{t.totalTTC}:</span>
              <span>€124.99</span>
            </div>
          </div>
        </div>
      )}

      {/* PAYMENT INFO */}
      {isVisible('payment_info') && (
        <div className="flex gap-4 mb-6 p-3 rounded" style={{ backgroundColor: '#f5f5f5' }}>
          <div className="flex-1">
            <div className="font-semibold mb-2" style={{ color: primary }}>{t.paymentInfo}</div>
            <div>{t.iban}: BE00 0000 0000 0000</div>
            <div>{t.bic}: GEBABEBB</div>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-white border rounded flex items-center justify-center text-gray-400 text-xs">
              QR
            </div>
            <div className="text-xs text-gray-500 mt-1">{t.scanToPay}</div>
          </div>
        </div>
      )}

      {/* FOOTER */}
      {isVisible('footer') && (
        <div className="text-center text-gray-500 pt-4 border-t">
          {t.footer}
        </div>
      )}
    </div>
  );
};

// Composant principal
const TemplateEditor = ({ template, onSave, onClose, language = 'fr' }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('colors');
  
  // Initialiser avec les valeurs du template ou les valeurs par défaut
  const [editedTemplate, setEditedTemplate] = useState({
    name: template?.name || '',
    name_nl: template?.name_nl || '',
    description: template?.description || '',
    description_nl: template?.description_nl || '',
    primary_color: template?.primary_color || '#8B4513',
    secondary_color: template?.secondary_color || '#D2691E',
    accent_color: template?.accent_color || '#F5DEB3',
    logo_url: template?.logo_url || null,
    logo_width: template?.logo_width || 40,
    logo_height: template?.logo_height || 25,
    logo_original_width: template?.logo_original_width || null,
    logo_original_height: template?.logo_original_height || null,
    elements: template?.elements?.length > 0 ? template.elements : DEFAULT_ELEMENTS,
    is_default: template?.is_default || false,
    // Conserver les autres champs
    id: template?.id,
    style_type: template?.style_type || 'moderne',
    background_color: template?.background_color || '#FFFFFF',
    header_font: template?.header_font || 'Helvetica',
    body_font: template?.body_font || 'Helvetica',
    header_size: template?.header_size || 24,
    body_size: template?.body_size || 10,
    logo_position: template?.logo_position || 'top-left',
    show_qr_code: template?.show_qr_code !== false,
    show_bank_info: template?.show_bank_info !== false,
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const t = language === 'fr' ? {
    title: 'Éditeur de Template',
    colors: 'Couleurs',
    logo: 'Logo',
    elements: 'Éléments',
    options: 'Options',
    save: 'Enregistrer',
    cancel: 'Annuler',
    templateName: 'Nom du template',
    description: 'Description',
    primaryColor: 'Couleur principale',
    primaryColorHint: 'Titres, en-têtes de tableau',
    secondaryColor: 'Couleur secondaire',
    secondaryColorHint: 'TVA, accents',
    accentColor: 'Couleur d\'accent',
    accentColorHint: 'Fonds, lignes alternées',
    uploadLogo: 'Télécharger un logo',
    removeLogo: 'Supprimer',
    logoHint: 'PNG ou JPG, max 2MB',
    dragToReorder: 'Glissez pour réorganiser',
    preview: 'Aperçu',
    setDefault: 'Définir par défaut',
  } : {
    title: 'Template Editor',
    colors: 'Kleuren',
    logo: 'Logo',
    elements: 'Elementen',
    options: 'Opties',
    save: 'Opslaan',
    cancel: 'Annuleren',
    templateName: 'Sjabloonnaam',
    description: 'Beschrijving',
    primaryColor: 'Hoofdkleur',
    primaryColorHint: 'Titels, tabelkoppen',
    secondaryColor: 'Secundaire kleur',
    secondaryColorHint: 'BTW, accenten',
    accentColor: 'Accentkleur',
    accentColorHint: 'Achtergronden, alternerende rijen',
    uploadLogo: 'Logo uploaden',
    removeLogo: 'Verwijderen',
    logoHint: 'PNG of JPG, max 2MB',
    dragToReorder: 'Sleep om te herschikken',
    preview: 'Voorbeeld',
    setDefault: 'Als standaard instellen',
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setEditedTemplate(prev => {
        const oldIndex = prev.elements.findIndex(e => e.id === active.id);
        const newIndex = prev.elements.findIndex(e => e.id === over.id);
        return {
          ...prev,
          elements: arrayMove(prev.elements, oldIndex, newIndex),
        };
      });
    }
  };

  const toggleVisibility = (elementId) => {
    setEditedTemplate(prev => ({
      ...prev,
      elements: prev.elements.map(el =>
        el.id === elementId ? { ...el, visible: !el.visible } : el
      ),
    }));
  };

  const updateField = (field, value) => {
    setEditedTemplate(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      alert(language === 'fr' ? 'Veuillez sélectionner une image' : 'Selecteer een afbeelding');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      alert(language === 'fr' ? 'Fichier trop volumineux (max 2MB)' : 'Bestand te groot (max 2MB)');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('ebb_token');
      const response = await fetch(`${BACKEND_URL}/api/upload/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });
      
      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      console.log('Logo uploaded:', data);
      
      // Stocker l'URL et les dimensions originales
      setEditedTemplate(prev => ({
        ...prev,
        logo_url: data.url,
        logo_original_width: data.width,
        logo_original_height: data.height,
        // Calculer une largeur par défaut (40mm) si on a les dimensions
        logo_width: 40,
      }));
    } catch (error) {
      console.error('Logo upload error:', error);
      alert(language === 'fr' ? 'Erreur lors du téléchargement' : 'Upload mislukt');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = () => {
    if (!editedTemplate.name.trim()) {
      alert(language === 'fr' ? 'Veuillez entrer un nom' : 'Voer een naam in');
      return;
    }
    
    // DEBUG - Log complet de ce qui va être sauvegardé
    console.log('========== TEMPLATE EDITOR SAVE ==========');
    console.log('Full editedTemplate:', JSON.stringify(editedTemplate, null, 2));
    console.log('Colors:', {
      primary: editedTemplate.primary_color,
      secondary: editedTemplate.secondary_color,
      accent: editedTemplate.accent_color,
    });
    console.log('Logo:', editedTemplate.logo_url);
    console.log('Elements count:', editedTemplate.elements?.length);
    console.log('Elements:', JSON.stringify(editedTemplate.elements, null, 2));
    console.log('==========================================');
    
    onSave(editedTemplate);
  };

  const tabs = [
    { id: 'colors', icon: Palette, label: t.colors },
    { id: 'logo', icon: Image, label: t.logo },
    { id: 'elements', icon: LayoutList, label: t.elements },
    { id: 'options', icon: Settings, label: t.options },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-bold text-gray-900">{t.title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Settings */}
          <div className="w-80 border-r flex flex-col">
            {/* Tabs */}
            <div className="flex border-b">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 p-3 text-sm flex flex-col items-center gap-1 ${
                    activeTab === tab.id
                      ? 'bg-amber-50 text-amber-700 border-b-2 border-amber-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {/* Colors Tab */}
              {activeTab === 'colors' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t.templateName} *
                    </label>
                    <input
                      type="text"
                      value={editedTemplate.name}
                      onChange={(e) => updateField('name', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Mon template"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t.primaryColor}
                    </label>
                    <p className="text-xs text-gray-500 mb-2">{t.primaryColorHint}</p>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={editedTemplate.primary_color}
                        onChange={(e) => updateField('primary_color', e.target.value)}
                        className="w-12 h-10 rounded border cursor-pointer"
                      />
                      <input
                        type="text"
                        value={editedTemplate.primary_color}
                        onChange={(e) => updateField('primary_color', e.target.value)}
                        className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t.secondaryColor}
                    </label>
                    <p className="text-xs text-gray-500 mb-2">{t.secondaryColorHint}</p>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={editedTemplate.secondary_color}
                        onChange={(e) => updateField('secondary_color', e.target.value)}
                        className="w-12 h-10 rounded border cursor-pointer"
                      />
                      <input
                        type="text"
                        value={editedTemplate.secondary_color}
                        onChange={(e) => updateField('secondary_color', e.target.value)}
                        className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t.accentColor}
                    </label>
                    <p className="text-xs text-gray-500 mb-2">{t.accentColorHint}</p>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={editedTemplate.accent_color}
                        onChange={(e) => updateField('accent_color', e.target.value)}
                        className="w-12 h-10 rounded border cursor-pointer"
                      />
                      <input
                        type="text"
                        value={editedTemplate.accent_color}
                        onChange={(e) => updateField('accent_color', e.target.value)}
                        className="flex-1 px-3 py-2 border rounded-lg text-sm font-mono"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Logo Tab */}
              {activeTab === 'logo' && (
                <div className="space-y-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    {editedTemplate.logo_url ? (
                      <div className="space-y-4">
                        <img 
                          src={getFullLogoUrl(editedTemplate.logo_url)} 
                          alt="Logo" 
                          className="max-h-24 mx-auto object-contain"
                          onError={(e) => {
                            console.log('Logo display error:', editedTemplate.logo_url);
                            e.target.alt = 'Erreur de chargement';
                          }}
                        />
                        {editedTemplate.logo_original_width && (
                          <p className="text-xs text-gray-500">
                            {language === 'fr' ? 'Original' : 'Origineel'}: {editedTemplate.logo_original_width} x {editedTemplate.logo_original_height} px
                          </p>
                        )}
                        <button
                          onClick={() => {
                            setEditedTemplate(prev => ({
                              ...prev,
                              logo_url: null,
                              logo_original_width: null,
                              logo_original_height: null,
                            }));
                          }}
                          className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center gap-2 mx-auto"
                        >
                          <Trash2 className="w-4 h-4" />
                          {t.removeLogo}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="w-16 h-16 mx-auto bg-gray-100 rounded-lg flex items-center justify-center">
                          <Image className="w-8 h-8 text-gray-400" />
                        </div>
                        <div>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            className="hidden"
                          />
                          <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2 mx-auto disabled:opacity-50"
                          >
                            <Upload className="w-4 h-4" />
                            {uploading ? '...' : t.uploadLogo}
                          </button>
                          <p className="text-xs text-gray-500 mt-2">{t.logoHint}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Contrôles de taille du logo */}
                  {editedTemplate.logo_url && (
                    <div className="space-y-3 pt-4 border-t">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {language === 'fr' ? 'Largeur du logo' : 'Logo breedte'}: {editedTemplate.logo_width || 40} mm
                        </label>
                        <input
                          type="range"
                          min="20"
                          max="80"
                          step="5"
                          value={editedTemplate.logo_width || 40}
                          onChange={(e) => updateField('logo_width', parseInt(e.target.value))}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>20mm</span>
                          <span>50mm</span>
                          <span>80mm</span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        {language === 'fr' 
                          ? 'La hauteur est calculée automatiquement pour garder les proportions' 
                          : 'De hoogte wordt automatisch berekend om de verhoudingen te behouden'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Elements Tab */}
              {activeTab === 'elements' && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-500 mb-4">{t.dragToReorder}</p>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={editedTemplate.elements.map(e => e.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {editedTemplate.elements.map(element => (
                        <SortableElement
                          key={element.id}
                          element={element}
                          language={language}
                          onToggleVisibility={toggleVisibility}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>
              )}

              {/* Options Tab */}
              {activeTab === 'options' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t.description}
                    </label>
                    <textarea
                      value={editedTemplate.description}
                      onChange={(e) => updateField('description', e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg"
                      rows={3}
                    />
                  </div>

                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editedTemplate.is_default}
                      onChange={(e) => updateField('is_default', e.target.checked)}
                      className="w-5 h-5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <span className="text-sm font-medium text-gray-700">{t.setDefault}</span>
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className="flex-1 bg-gray-100 p-6 overflow-y-auto">
            <h3 className="text-sm font-semibold text-gray-600 mb-4">{t.preview}</h3>
            <TemplatePreview template={editedTemplate} language={language} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            {t.cancel}
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {t.save}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;
